import { Music, Sound, MusicListener } from './java/Slick';
import { Random, shuffle } from './java/Random';

export class Track {
  readonly key: string;
  readonly displayName: string;
  readonly music: Music;
  readonly duration: number;
  readonly bpm: number;

  constructor(key: string, displayName: string, music: Music, duration: number, bpm: number) {
    this.key = key;
    this.displayName = displayName;
    this.music = music;
    this.duration = duration;
    this.bpm = bpm;
  }
}

export class AudioPlayer implements MusicListener {

  private static sounds: Map<string, Sound> = new Map<string, Sound>();
  private static tracks: Track[] = [];
  private static currentTrackIndex = 0;
  private static volume = 0.10;
  private static paused = false;
  private static stopped = true;
  private static trackEnded = false;
  private static trackStartTime = 0;
  private static instance: AudioPlayer = new AudioPlayer();

  static load(): void {
    try {
      AudioPlayer.addTrack('energy_drink', 'Virtual Riot - Energy Drink', 'Virtual Riot - Energy Drink.ogg', 303, 150);
      AudioPlayer.addTrack('press_start', 'MDK - Press Start', 'MDK - Press Start.ogg', 263, 160);
      AudioPlayer.addTrack('hellcat', 'Desmeon - Hellcat', 'Desmeon - Hellcat.ogg', 226, 150);

      AudioPlayer.addTrack('fingerbang', 'MDK - Fingerbang', 'MDK - Fingerbang.ogg', 228, 150);
      AudioPlayer.addTrack('disconnected', 'Pegboard Nerds - Disconnected', 'Pegboard Nerds - Disconnected.ogg', 242, 128);
      AudioPlayer.addTrack('levels', 'Avicii - Levels (Skrillex Remix)', 'Avicii - Levels (Skrillex remix).ogg', 210, 128);

      // Register listener on all tracks
      for (const t of AudioPlayer.tracks) {
        t.music.addListener(AudioPlayer.instance);
      }

      AudioPlayer.buildPlaylist();
    } catch (e) {
      console.error(e);
    }
  }

  // MusicListener callback — fired by Slick2D when a track ends
  musicEnded(_music: Music): void {
    AudioPlayer.trackEnded = true;
  }

  musicSwapped(_oldMusic: Music, _newMusic: Music): void {
  }

  private static addTrack(key: string, displayName: string, path: string, duration: number, bpm: number): void {
    AudioPlayer.tracks.push(new Track(key, displayName, new Music(path), duration, bpm));
  }

  private static buildPlaylist(): void {
    const r = new Random();
    const opener = AudioPlayer.tracks[r.nextInt(Math.min(4, AudioPlayer.tracks.length))];
    shuffle(AudioPlayer.tracks, r);
    const idx = AudioPlayer.tracks.indexOf(opener);
    if (idx >= 0) AudioPlayer.tracks.splice(idx, 1);
    AudioPlayer.tracks.splice(0, 0, opener);
    AudioPlayer.currentTrackIndex = 0;
  }

  static play(): void {
    if (AudioPlayer.tracks.length === 0) return;
    const track = AudioPlayer.getCurrentTrack();
    if (AudioPlayer.paused) {
      track.music.resume();
      AudioPlayer.paused = false;
    } else {
      track.music.play();
      track.music.setVolume(AudioPlayer.volume);
      AudioPlayer.trackStartTime = Date.now();
    }
    AudioPlayer.stopped = false;
    AudioPlayer.trackEnded = false;
  }

  static pause(): void {
    if (AudioPlayer.tracks.length === 0 || AudioPlayer.stopped) return;
    const track = AudioPlayer.getCurrentTrack();
    if (track.music.playing()) {
      track.music.pause();
      AudioPlayer.paused = true;
    }
  }

  static stop(): void {
    if (AudioPlayer.tracks.length === 0) return;
    AudioPlayer.getCurrentTrack().music.stop();
    AudioPlayer.paused = false;
    AudioPlayer.stopped = true;
    AudioPlayer.trackEnded = false;
  }

  static checkAutoAdvance(): void {
    if (AudioPlayer.tracks.length === 0 || AudioPlayer.stopped || AudioPlayer.paused) return;

    let ended = AudioPlayer.trackEnded;

    // Timer-based fallback — if elapsed time exceeds track duration
    if (!ended && AudioPlayer.trackStartTime > 0) {
      const elapsed = Date.now() - AudioPlayer.trackStartTime;
      const durationMs = AudioPlayer.getCurrentTrack().duration * 1000;
      if (elapsed >= durationMs - 500) {
        ended = true;
      }
    }

    if (ended) {
      AudioPlayer.trackEnded = false;
      try { AudioPlayer.getCurrentTrack().music.stop(); } catch { /* ignore */ }
      AudioPlayer.currentTrackIndex = (AudioPlayer.currentTrackIndex + 1) % AudioPlayer.tracks.length;
      try {
        const next = AudioPlayer.getCurrentTrack();
        next.music.play();
        next.music.setVolume(AudioPlayer.volume);
        AudioPlayer.trackStartTime = Date.now();
      } catch (e) { console.error(e); }
      AudioPlayer.stopped = false;
      AudioPlayer.paused = false;
    }
  }

  static togglePlayPause(): void {
    if (AudioPlayer.isPlaying()) {
      AudioPlayer.pause();
    } else {
      AudioPlayer.play();
    }
  }

  static nextTrack(): void {
    if (AudioPlayer.tracks.length === 0) return;
    const wasPlaying = AudioPlayer.isPlaying();
    AudioPlayer.stop();
    AudioPlayer.currentTrackIndex = (AudioPlayer.currentTrackIndex + 1) % AudioPlayer.tracks.length;
    if (wasPlaying) AudioPlayer.play();
  }

  static previousTrack(): void {
    if (AudioPlayer.tracks.length === 0) return;
    const wasPlaying = AudioPlayer.isPlaying();
    AudioPlayer.stop();
    AudioPlayer.currentTrackIndex = (AudioPlayer.currentTrackIndex - 1 + AudioPlayer.tracks.length) % AudioPlayer.tracks.length;
    if (wasPlaying) AudioPlayer.play();
  }

  static setVolume(vol: number): void {
    AudioPlayer.volume = Math.max(0, Math.min(1, vol));
    if (AudioPlayer.tracks.length !== 0 && !AudioPlayer.stopped) {
      AudioPlayer.getCurrentTrack().music.setVolume(AudioPlayer.volume);
    }
  }

  static getVolume(): number {
    return AudioPlayer.volume;
  }

  static seekTo(seconds: number): void {
    if (AudioPlayer.tracks.length === 0 || AudioPlayer.stopped) return;
    AudioPlayer.getCurrentTrack().music.setPosition(seconds);
    AudioPlayer.trackStartTime = Date.now() - Math.trunc(seconds * 1000);
  }

  static getPosition(): number {
    if (AudioPlayer.tracks.length === 0 || AudioPlayer.stopped) return 0;
    return AudioPlayer.getCurrentTrack().music.getPosition();
  }

  static isPlaying(): boolean {
    if (AudioPlayer.tracks.length === 0) return false;
    return AudioPlayer.getCurrentTrack().music.playing();
  }

  static isPaused(): boolean {
    return AudioPlayer.paused;
  }

  static isStopped(): boolean {
    return AudioPlayer.stopped;
  }

  static getCurrentTrack(): Track {
    return AudioPlayer.tracks[AudioPlayer.currentTrackIndex];
  }

  static getCurrentTrackIndex(): number {
    return AudioPlayer.currentTrackIndex;
  }

  static getTrackCount(): number {
    return AudioPlayer.tracks.length;
  }

  static getSound(key: string): Sound | undefined {
    return AudioPlayer.sounds.get(key);
  }

  static getBeatPulse(): number {
    if (AudioPlayer.tracks.length === 0 || AudioPlayer.stopped || !AudioPlayer.isPlaying()) return 0;
    const track = AudioPlayer.getCurrentTrack();
    const pos = AudioPlayer.getPosition();
    const beatsPerSec = track.bpm / 60;
    const beatPhase = (pos * beatsPerSec) % 1;
    return Math.pow(1.0 - beatPhase, 3.5);
  }

  /** Java nested class was AudioPlayer.Track — keep that name resolvable as a value. */
  static readonly Track = Track;
}

type TrackImpl = Track;

// ...and as a type: `AudioPlayer.Track` in a type position.
export namespace AudioPlayer {
  export type Track = TrackImpl;
}
